/**
 * Settings → "Režim rodiny" card (Phase 3).
 *
 * Lets a PARENT switch the family's operating mode between `family` (intact
 * household) and `separated` (separate households — unlocks custody, agreements,
 * audit, cost-splitting and the message log via the registry `modes` gate).
 *
 * Deliberately low-key and high-friction: trusted members never see it, the
 * switch is a two-step confirm, and the copy reassures that no data is lost and
 * the flip is reversible. The actual switch is a single audited endpoint
 * (`api.setFamilyMode` → POST /families/:id/mode); we then `reloadMe()` so the
 * new mode propagates to the sidebar/module gate everywhere.
 */
import { useState } from 'react';
import type { FamilyMode } from '@solandia/core';
import { useI18n } from '../i18n';
import { useApi } from '../api/ApiProvider';
import { useFamily } from '../family/FamilyContext';
import { Dialog } from '../components/ui';

export function FamilyModeCard() {
  const { t } = useI18n();
  const api = useApi();
  const { family, membership, reloadFamily } = useFamily();

  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [switched, setSwitched] = useState(false);

  // Trusted members never see the mode control — it is a parent-only decision.
  if (membership.role !== 'parent') return null;

  const m = t.mode;
  const isFamily = family.mode === 'family';
  const target: FamilyMode = isFamily ? 'separated' : 'family';

  async function confirmSwitch() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.setFamilyMode(family.id, target);
      // Refetch the authoritative family record so `family.mode` (and the
      // module gate) updates everywhere.
      reloadFamily();
      setConfirming(false);
      setSwitched(true);
    } catch {
      setError(t.errors.generic);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card stack" aria-labelledby="settings-mode-heading">
      <h2 id="settings-mode-heading" className="section-title">
        {m.cardTitle}
      </h2>
      <p className="lead">{m.cardLead}</p>

      <div className="field">
        <span className="field-label">{m.currentLabel}</span>
        <div>
          <strong>{isFamily ? m.familyLabel : m.separatedLabel}</strong>
          <p className="text-sm muted">{isFamily ? m.familyDesc : m.separatedDesc}</p>
        </div>
      </div>

      <div className="row" style={{ justifyContent: 'flex-start' }}>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => {
            setSwitched(false);
            setError(null);
            setConfirming(true);
          }}
        >
          {isFamily ? m.switchToSeparated : m.switchToFamily}
        </button>
        {/* Polite live region: announces the change without stealing focus. */}
        <span role="status" aria-live="polite" className="text-sm muted">
          {switched ? m.switched : ''}
        </span>
      </div>

      {confirming ? (
        <Dialog
          title={isFamily ? m.confirmToSeparatedTitle : m.confirmToFamilyTitle}
          onClose={() => {
            if (!busy) setConfirming(false);
          }}
          closeLabel={t.common.close}
        >
          <p className="lead">
            {isFamily ? m.confirmToSeparatedLead : m.confirmToFamilyLead}
          </p>
          {isFamily ? (
            <p className="text-sm muted">{m.confirmToSeparatedNote}</p>
          ) : null}

          {error ? (
            <div className="banner banner-danger" role="alert">
              <span aria-hidden="true">⚠️</span>
              <div className="grow">{error}</div>
            </div>
          ) : null}

          <div className="row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setConfirming(false)}
              disabled={busy}
            >
              {t.common.cancel}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={confirmSwitch}
              disabled={busy}
            >
              {busy
                ? m.switching
                : isFamily
                  ? m.confirmToSeparatedAction
                  : m.confirmToFamilyAction}
            </button>
          </div>
        </Dialog>
      ) : null}
    </section>
  );
}
